import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { useAuthStore } from '@/stores/auth.store'
import { legalService, TermsDocument } from '@/services/legal.service'
import { Download, LogOut, AlertTriangle, Loader2, Check } from 'lucide-react'

interface CheckboxItem {
  id: string
  label: string
}

const REQUIRED_CHECKBOXES: CheckboxItem[] = [
  { id: 'cb1_condizioni_generali', label: 'Accetto le Condizioni Generali iXen/TradeBridge.' },
  { id: 'cb2_autonomia_no_garanzia', label: 'Confermo che opero in totale autonomia e che iXen non garantisce risultati, rendimenti o payout.' },
  { id: 'cb3_terze_parti', label: 'Accetto che prop firm, broker e piattaforme (inclusi MT4/MT5) sono Terze Parti e che violazioni per errore, incuria o disservizi tecnici non sono responsabilità di iXen.' },
  { id: 'cb4_rischio_controparte', label: 'Accetto il rischio controparte e riconosco che iXen non garantisce solvibilità o pagamenti delle Terze Parti (payout/prelievi).' },
  { id: 'cb5_regole_canali_ufficiali', label: 'Dichiaro di aver compreso e accettato la clausola "Regole e aggiornamenti — Canali Ufficiali".' },
  { id: 'cb6_anti_copia', label: 'Accetto le clausole Anti-Copia (reverse engineering, scraping, AI training, opere derivate, benchmark).' },
  { id: 'cb7_anti_imitazione_riservatezza', label: 'Accetto il patto anti-imitazione, il divieto di uso indebito del know-how, il divieto di non sollecitazione/non circonvenzione, gli obblighi di riservatezza e le relative penali.' },
  { id: 'cb8_limitazione_responsabilita', label: 'Accetto la limitazione di responsabilità e la manleva.' },
  { id: 'cb9_legge_e_foro', label: 'Accetto legge applicabile e foro.' },
  { id: 'cb10_funzionalita_automatiche', label: 'Accetto l\'utilizzo di funzionalità automatiche e sistemi di supporto (es. TradeBridgePro, Yomi, trailing) e riconosco che la responsabilità operativa resta interamente a mio carico.' },
  { id: 'cb11_proprieta_intellettuale', label: 'Dichiaro di aver letto e compreso le clausole relative a proprietà intellettuale, anti-copia, riservatezza, penali e limitazioni di responsabilità.' },
  { id: 'cb12_demo_first', label: 'Mi impegno a operare in DEMO fino a quando avrò imparato a gestire manualmente ordini, lotti, SL e TP sia da PC sia da smartphone ("Demo First"). Mi impegno a usare l\'automazione o operare su conti reali solo dopo aver acquisito queste competenze.' },
  { id: 'cb13_attivazione_immediata_recesso', label: 'Richiedo l\'attivazione immediata del servizio digitale e riconosco che, una volta iniziata l\'esecuzione, il diritto di recesso potrà essere escluso o limitato nei casi previsti dalla legge applicabile.' },
]

interface Props {
  /** Se true, modale in sola lettura (consultazione dal menu profilo) — niente checkbox/accetta/esci */
  readOnly?: boolean
  /** Chiamato solo in modalità readOnly per chiudere */
  onClose?: () => void
  /** Chiamato dopo accettazione riuscita */
  onAccepted?: () => void
}

export function TermsAcceptanceModal({ readOnly = false, onClose, onAccepted }: Props) {
  const { logout, updateUser, user } = useAuthStore()
  const [doc, setDoc] = useState<TermsDocument | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [scrolledToEnd, setScrolledToEnd] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [checks, setChecks] = useState<Record<string, boolean>>({})
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    legalService
      .getDocument()
      .then((d) => {
        if (!alive) return
        setDoc(d)
        setLoading(false)
      })
      .catch((e) => {
        if (!alive) return
        setError(String(e))
        setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [])

  const handleScroll = () => {
    const el = scrollRef.current
    if (!el) return
    const reachedEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 16
    if (reachedEnd && !scrolledToEnd) setScrolledToEnd(true)
  }

  const allChecked = REQUIRED_CHECKBOXES.every((c) => checks[c.id] === true)
  const canSubmit = !readOnly && !submitting && scrolledToEnd && allChecked

  const handleAccept = async () => {
    if (!doc) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await legalService.accept(doc.version, checks)
      updateUser({
        terms_accepted_at: res.accepted_at,
        terms_version_accepted: res.version,
      })
      onAccepted?.()
    } catch (e: any) {
      setError(e?.message || 'Errore durante l\'accettazione')
    } finally {
      setSubmitting(false)
    }
  }

  const handleLogout = () => {
    logout()
    window.location.href = '/login'
  }

  // Conta dei check spuntati per il badge nell'header del pannello
  const checkedCount = REQUIRED_CHECKBOXES.reduce((n, c) => n + (checks[c.id] ? 1 : 0), 0)
  const totalCount = REQUIRED_CHECKBOXES.length

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-card text-card-foreground w-full max-w-4xl h-[92vh] rounded-lg border shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b shrink-0">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              {readOnly ? (
                'Termini e Condizioni iXen'
              ) : (
                <>
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Accettazione Termini & Condizioni
                </>
              )}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {doc
                ? `Versione ${doc.version}`
                : readOnly
                ? 'Consultazione contratto'
                : 'Per accedere all\'area trading devi leggere e accettare il contratto qui sotto.'}
            </p>
          </div>
          {doc?.pdf_url && (
            <a
              href={doc.pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm inline-flex items-center gap-1 text-blue-500 hover:underline shrink-0"
            >
              <Download className="w-4 h-4" />
              Scarica PDF
            </a>
          )}
        </div>

        {/* Modalità accettazione: split verticale 50/50 (contratto sopra, check sotto) */}
        {!readOnly && (
          <div className="flex-1 min-h-0 flex flex-col">
            {/* ── Pannello 1: Contratto (50%) ─────────────────────────────────── */}
            <div className="basis-1/2 min-h-0 flex flex-col border-b">
              <div className="px-5 py-2 border-b bg-muted/40 text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center justify-between shrink-0">
                <span>📄 Contratto di Licenza</span>
                {scrolledToEnd ? (
                  <span className="inline-flex items-center gap-1 text-emerald-500 normal-case font-medium">
                    <Check className="w-3 h-3" /> Letto fino in fondo
                  </span>
                ) : (
                  <span className="text-amber-400 normal-case font-medium">Scorri fino alla fine</span>
                )}
              </div>
              <div
                ref={scrollRef}
                onScroll={handleScroll}
                className="flex-1 min-h-0 overflow-y-auto px-5 py-4 text-sm leading-relaxed bg-muted/20"
              >
                {loading && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" /> Caricamento contratto...
                  </div>
                )}
                {error && !doc && (
                  <div className="text-red-500">Errore caricamento: {error}</div>
                )}
                {doc && (
                  <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">
                    {doc.text}
                  </pre>
                )}
              </div>
            </div>

            {/* ── Pannello 2: Checkbox accettazione (50%) ──────────────────────── */}
            <div className="basis-1/2 min-h-0 flex flex-col">
              <div className="px-5 py-2 border-b bg-muted/40 text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center justify-between shrink-0">
                <span>✅ Conferme richieste</span>
                <span className={`normal-case font-bold ${checkedCount === totalCount ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                  {checkedCount} / {totalCount}
                </span>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto px-5 py-3 space-y-2">
                {!scrolledToEnd && (
                  <div className="rounded-md bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-sm text-amber-300 flex items-center gap-2 sticky top-0 z-10 backdrop-blur">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    Scorri fino alla fine del contratto per poter spuntare le caselle.
                  </div>
                )}
                {REQUIRED_CHECKBOXES.map((cb, idx) => (
                  <label
                    key={cb.id}
                    htmlFor={cb.id}
                    className={`flex items-start gap-2 text-sm cursor-pointer p-2 rounded transition-colors ${
                      !scrolledToEnd
                        ? 'opacity-50 cursor-not-allowed'
                        : checks[cb.id]
                        ? 'bg-emerald-500/10 hover:bg-emerald-500/15'
                        : 'hover:bg-muted/50'
                    }`}
                  >
                    <Checkbox
                      id={cb.id}
                      disabled={!scrolledToEnd}
                      checked={!!checks[cb.id]}
                      onCheckedChange={(v) =>
                        setChecks((prev) => ({ ...prev, [cb.id]: v === true }))
                      }
                      className="mt-0.5"
                    />
                    <span className="leading-snug">
                      <span className="font-medium text-foreground/70 mr-1">{idx + 1}.</span>
                      {cb.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Modalità sola lettura: solo contratto (occupa tutto) */}
        {readOnly && (
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex-1 min-h-0 overflow-y-auto px-5 py-4 text-sm leading-relaxed border-b bg-muted/30"
          >
            {loading && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Caricamento contratto...
              </div>
            )}
            {error && !doc && (
              <div className="text-red-500">Errore caricamento: {error}</div>
            )}
            {doc && (
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">
                {doc.text}
              </pre>
            )}
          </div>
        )}

        {/* Footer pulsanti azione (fuori dallo scroll) */}
        {!readOnly && (
          <div className="p-4 border-t shrink-0 space-y-3">
            {error && (
              <div className="text-sm text-red-500 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
                {error}
              </div>
            )}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <Button
                variant="ghost"
                onClick={handleLogout}
                disabled={submitting}
                className="text-red-500 hover:text-red-400"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
              <Button
                onClick={handleAccept}
                disabled={!canSubmit}
                className="bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-600 hover:to-sky-600 text-white font-semibold"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Invio...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    ACCETTO E ATTIVO LA LICENZA
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Footer in modalità sola lettura */}
        {readOnly && (
          <div className="p-4 flex items-center justify-between border-t shrink-0">
            <div className="text-xs text-muted-foreground">
              {user?.terms_accepted_at
                ? `Accettato il ${new Date(user.terms_accepted_at).toLocaleString('it-IT')}${
                    user.terms_version_accepted ? ` — versione ${user.terms_version_accepted}` : ''
                  }`
                : 'Non ancora accettato.'}
            </div>
            <Button onClick={onClose} variant="outline">
              Chiudi
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
